import classNames from 'classnames';
import { FileUploadedList } from 'components/molecules/FileUploadedList';
import { isEmpty } from 'lodash';
import groupBy from 'lodash/groupBy';
import type { FC } from 'react';
import { useMemo } from 'react';
import type { FileUploaderProps } from './FileUploader.types';
import { DropzoneControlled } from 'components/molecules/Dropzone/Dropzone.component';
import useCustomDropzoneStore, {
  CustomDropzoneConfig,
} from 'hooks/useCustomDropzoneStore';
import useKeyValueState from 'hooks/useKeyValueState';
import { FileUploadStatus } from 'hooks/filesUploadStoreFactory';
import { globalyAcceptedFileUploaderTypes, maxFileSize } from 'utils/constants';

const ControlledFileUploader: FC<FileUploaderProps> = ({
  dropzoneProps,
  filesListProps,
  className,
  color,
  renderUploadedFileItem,
  filesStateApi,
  dropzoneAPI,
  processFileUpload,
  hideDropzoneWhileUploading,
  filesListContainerClassName,
  asDefault,
  renderUploadList,
  renderExtra,
}) => {
  const filteredFiles = useMemo(() => {
    if (!filesListProps?.hideDoneFiles) return filesStateApi.values;
    return filesStateApi.values.filter(
      (fileState) => fileState.status !== FileUploadStatus.done,
    );
  }, [filesListProps?.hideDoneFiles, filesStateApi.values]);

  const fileUploaderMeta = useMemo(
    () => ({
      ...groupBy(filteredFiles, 'status'),
      isEmpty: isEmpty(filteredFiles),
    }),
    [filteredFiles],
  );

  return (
    <div className={classNames('flex gap-4 h-full', className)}>
      {hideDropzoneWhileUploading && filteredFiles.length > 0 ? null : (
        <DropzoneControlled
          dropzoneAPI={dropzoneAPI}
          disabled={
            dropzoneProps.getDropOptions?.(fileUploaderMeta)?.disabled ??
            dropzoneProps.dropOptions?.disabled
          }
          color={color || dropzoneProps.color}
          render={(dropzoneMeta) =>
            dropzoneProps.render(
              dropzoneMeta,
              fileUploaderMeta,
              processFileUpload,
            )
          }
          asDefault={asDefault}
          className={classNames(
            'flex-1 flex-shrink-0',
            dropzoneProps.className,
          )}
          renderWrapperClassName={dropzoneProps.renderWrapperClassName}
          onClick={dropzoneProps.onClick}
        />
      )}

      {renderExtra?.(processFileUpload, fileUploaderMeta)}

      {filteredFiles.length > 0 ? (
        renderUploadList ? (
          renderUploadList(filteredFiles, fileUploaderMeta)
        ) : (
          <div
            className={classNames(
              'flex flex-1 flex-col px-2 pb-10 md:pb-0 overflow-y-auto force-scroll-style',
              filesListContainerClassName,
            )}
          >
            <div className="flex flex-1 flex-col">
              {filesListProps?.renderHeader?.(fileUploaderMeta)}
              <FileUploadedList
                color={color || filesListProps?.color}
                className="flex flex-col gap-2"
                onRetry={processFileUpload}
                files={filteredFiles}
                hideProgressBarOnDone={filesListProps?.hideProgressBarOnDone}
                groupDoneFiles={filesListProps?.groupDoneFiles}
                groupFailedFiles={filesListProps?.groupFailedFiles}
                renderUploadedFileItem={renderUploadedFileItem}
                onFileReview={filesListProps?.onFileReview}
              />
            </div>
            {filesListProps?.renderFooter?.(fileUploaderMeta)}
          </div>
        )
      ) : null}
    </div>
  );
};

const FileUploader = ControlledFileUploader;

type LocalFileUploaderProps = Omit<
  FileUploaderProps,
  'filesStateApi' | 'dropzoneAPI' | 'processFileUpload'
> & {
  onUpload: CustomDropzoneConfig['onUpload'];
};

export const LocalFileUploader: FC<LocalFileUploaderProps> = (props) => {
  const filesStateApi = useKeyValueState();
  const APIs = useCustomDropzoneStore(
    {
      onUpload: props.onUpload,
      options: (filesMeta) => ({
        maxSize: maxFileSize,
        accept: globalyAcceptedFileUploaderTypes,
        ...props.dropzoneProps?.dropOptions,
        ...props.dropzoneProps.getDropOptions?.(filesMeta),
        onAfterDropAccepted: props.onAfterDropAccepted,
      }),
      defaultSchedulerOptions: {
        keepAlive: props.keepUploadingAfterDestroy,
        onComplete: props.afterPatchUpload,
      },
    },
    filesStateApi,
  );

  return (
    <ControlledFileUploader
      filesStateApi={APIs.storeAPI}
      dropzoneAPI={APIs.dropzoneAPI}
      processFileUpload={APIs.processFileUpload}
      {...props}
    />
  );
};

export default FileUploader;
